import {
  ArrowRight,
  BellRing,
  Building2,
  Calendar,
  Filter,
  HelpCircle,
  Loader2,
  Minus,
  Search,
  TrendingDown,
  TrendingUp,
  User,
  Users,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { MatterLink } from '@/components/entity-links';
import { MoBrainIcon } from '@/components/mobrain/MoBrainIcon';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MultiListbox } from '@/components/ui/catalyst/multi-listbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useGetMatterListMetadataQuery } from '@/services/api/mattersService';
import {
  type ClientSentiment,
  type PolicySearchUrgency,
  type RecommendedAction,
  type RiskOverviewMatterRow,
  type RiskOverviewParams,
  type RiskTier,
  type SentimentTrend,
  useGetRiskOverviewQuery,
} from '@/services/api/mobrainService';

type QueueFilter =
  | 'all'
  | 'urgent'
  | 'demand'
  | 'escalate'
  | 'gather'
  | 'policy'
  | 'stale'
  | 'monitor';
type GroupBy = 'recommendation' | 'firm' | 'attorney';

type RiskOverviewRow = RiskOverviewMatterRow & {
  representing_attorney_name: string | null;
  low_confidence_urgent: boolean;
  stale_or_incomplete: boolean;
  estimated_flag: string | null;
  policy_search_recommended: boolean;
  policy_search_urgency: PolicySearchUrgency | null;
  net_projection_below_25: boolean;
  missing_inputs_count: number;
  summary_short: string | null;
  coverage_tier: string | null;
  snapshot_created_at: string | null;
  confidence: number | null;
};

const ACTION_CONFIG: Record<
  RecommendedAction,
  { label: string; sortRank: number; badgeClass: string }
> = {
  URGENT_ATTORNEY_ESCALATION: {
    label: 'Urgent Attorney Escalation',
    sortRank: 500,
    badgeClass: 'bg-red-50 text-red-700 border-red-200',
  },
  MOVE_TO_DEMAND_REVIEW: {
    label: 'Move to Demand Review',
    sortRank: 400,
    badgeClass: 'bg-orange-50 text-orange-700 border-orange-200',
  },
  ESCALATE_CARE: {
    label: 'Escalate Care',
    sortRank: 350,
    badgeClass: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  CONTACT_CLIENT: {
    label: 'Contact Client',
    sortRank: 300,
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  CONFIRM_COVERAGE_LIMITS: {
    label: 'Confirm Coverage Limits',
    sortRank: 280,
    badgeClass: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  CONTINUE_AGGRESSIVE_CARE: {
    label: 'Continue Aggressive Care',
    sortRank: 200,
    badgeClass: 'bg-sky-50 text-sky-700 border-sky-200',
  },
  CONTINUE_CONSERVATIVE_CARE: {
    label: 'Continue Conservative Care',
    sortRank: 100,
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
};

const QUEUE_META: Record<
  QueueFilter,
  { label: string; sortRank: number; badgeClass: string }
> = {
  all: {
    label: 'All',
    sortRank: 0,
    badgeClass: 'bg-gray-50 text-gray-700 border-gray-200',
  },
  urgent: {
    label: 'Immediate',
    sortRank: 500,
    badgeClass: 'bg-red-50 text-red-700 border-red-200',
  },
  demand: {
    label: 'Demand Queue',
    sortRank: 400,
    badgeClass: 'bg-orange-50 text-orange-700 border-orange-200',
  },
  escalate: {
    label: 'Escalate Care',
    sortRank: 350,
    badgeClass: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  gather: {
    label: 'Needs Outreach',
    sortRank: 300,
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  policy: {
    label: 'Policy Search',
    sortRank: 250,
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  stale: {
    label: 'Refresh Snapshot',
    sortRank: 200,
    badgeClass: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  monitor: {
    label: 'Monitor',
    sortRank: 100,
    badgeClass: 'bg-gray-50 text-gray-700 border-gray-200',
  },
};

const POLICY_URGENCY_CLASS: Record<PolicySearchUrgency, string> = {
  LOW: 'bg-gray-50 text-gray-700 border-gray-200',
  MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
  HIGH: 'bg-orange-50 text-orange-700 border-orange-200',
  CRITICAL: 'bg-red-50 text-red-700 border-red-200',
};

const SENTIMENT_CLASS: Record<ClientSentiment, string> = {
  POSITIVE: 'bg-green-50 text-green-700 border-green-200',
  NEUTRAL: 'bg-gray-50 text-gray-700 border-gray-200',
  NEGATIVE: 'bg-red-50 text-red-700 border-red-200',
};

const TREND_ICON: Record<SentimentTrend, { icon: React.ElementType; className: string }> = {
  IMPROVING: { icon: TrendingUp, className: 'text-green-600' },
  STABLE: { icon: Minus, className: 'text-gray-500' },
  DECLINING: { icon: TrendingDown, className: 'text-red-600' },
};

const TIER_LABEL: Record<RiskTier, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
  UNKNOWN: 'Unknown',
};

function normalizeText(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function getSummaryOverview(summary: string | null): string | null {
  if (!summary) return null;
  const idx = summary.indexOf('Next steps:');
  if (idx === -1) return summary.trim();
  return summary.slice(0, idx).trim();
}

function getQueue(row: RiskOverviewRow): QueueFilter {
  if (
    row.recommended_action === 'URGENT_ATTORNEY_ESCALATION' ||
    row.low_confidence_urgent
  ) {
    return 'urgent';
  }
  if (row.stale_or_incomplete) return 'stale';
  if (row.recommended_action === 'MOVE_TO_DEMAND_REVIEW') return 'demand';
  if (row.recommended_action === 'ESCALATE_CARE') return 'escalate';
  if (
    row.recommended_action === 'CONTACT_CLIENT' ||
    row.recommended_action === 'CONFIRM_COVERAGE_LIMITS'
  )
    return 'gather';
  if (
    row.policy_search_recommended &&
    (row.policy_search_urgency === 'HIGH' ||
      row.policy_search_urgency === 'CRITICAL')
  ) {
    return 'policy';
  }
  return 'monitor';
}

function getRecommendationLabel(row: RiskOverviewRow): string {
  if (!row.recommended_action) return 'No Recommendation';
  return ACTION_CONFIG[row.recommended_action]?.label ?? row.recommended_action;
}

function getRecommendationRank(row: RiskOverviewRow): number {
  if (!row.recommended_action) return 0;
  return ACTION_CONFIG[row.recommended_action]?.sortRank ?? 0;
}

function getSortRank(row: RiskOverviewRow): number {
  const queueRank = QUEUE_META[getQueue(row)].sortRank;
  const recRank = getRecommendationRank(row);
  return queueRank + recRank;
}

function formatShortDate(value: string | null): string {
  if (!value) return '--';
  // Append T00:00:00 for date-only strings (e.g. "2025-03-15") to parse as
  // local midnight instead of UTC midnight, avoiding an off-by-one-day shift
  // for timezones west of UTC.
  const normalized = value.includes('T') ? value : `${value}T00:00:00`;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatUpdated(value: string | null): string {
  if (!value) return 'No snapshot time';
  const now = Date.now();
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return 'Invalid time';
  const diffMs = Math.max(now - ts, 0);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `Updated ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `Updated ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `Updated ${days}d ago`;
}

const LIMIT_SOURCE_LABEL: Record<string, string> = {
  confirmed: 'confirmed',
  estimated: 'estimated',
  uim_floor: 'UIM',
  state_minimum: 'state min',
  commercial_floor: 'commercial floor',
};

function formatLimit(
  limitForScoring: string | null,
  limitSource: string | null,
): string | null {
  if (!limitForScoring) return null;
  const num = Number(limitForScoring);
  if (Number.isNaN(num) || num <= 0) return null;
  let formatted: string;
  if (num >= 1_000_000) {
    formatted = `$${(num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1)}M`;
  } else if (num >= 1_000) {
    formatted = `$${(num / 1_000).toFixed(num % 1_000 === 0 ? 0 : 1)}K`;
  } else {
    formatted = `$${num.toLocaleString()}`;
  }
  const sourceLabel = limitSource ? (LIMIT_SOURCE_LABEL[limitSource] ?? limitSource) : '';
  return sourceLabel ? `${formatted} ${sourceLabel}` : formatted;
}

function groupRows(
  rows: RiskOverviewRow[],
  groupBy: GroupBy,
): Map<string, RiskOverviewRow[]> {
  const grouped = rows.reduce<Record<string, RiskOverviewRow[]>>((acc, row) => {
    let key = '';
    if (groupBy === 'recommendation') {
      key = getRecommendationLabel(row);
    } else if (groupBy === 'firm') {
      key = row.firm_name || 'Unknown Firm';
    } else {
      key = row.representing_attorney_name || 'Unassigned Attorney';
    }
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});

  if (groupBy === 'recommendation') {
    return new Map(
      Object.entries(grouped).sort((a, b) => {
        const aRank = getRecommendationRank(a[1][0]);
        const bRank = getRecommendationRank(b[1][0]);
        if (aRank !== bRank) return bRank - aRank;
        return a[0].localeCompare(b[0]);
      }),
    );
  }

  return new Map(
    Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])),
  );
}

const QueueMetricCard: React.FC<{
  label: string;
  value: number;
  sub: string;
  tone: 'critical' | 'warn' | 'neutral';
  active: boolean;
  onClick: () => void;
}> = ({ label, value, sub, tone, active, onClick }) => {
  let toneClass = 'border-gray-200 bg-gray-50';
  if (tone === 'critical') toneClass = 'border-red-200 bg-red-50';
  if (tone === 'warn') toneClass = 'border-amber-200 bg-amber-50';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-left rounded-xl border px-4 py-3 transition-all',
        toneClass,
        active
          ? 'ring-2 ring-primary/30 border-primary/40 shadow-sm'
          : 'hover:shadow-sm',
      )}
    >
      <div className="text-xl font-bold text-gray-900">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-700">
        {label}
      </div>
      <div className="text-xs text-gray-600 mt-1">{sub}</div>
    </button>
  );
};

const MatterTriageRow: React.FC<{ row: RiskOverviewRow }> = ({ row }) => {
  const actionClass = row.recommended_action
    ? (ACTION_CONFIG[row.recommended_action]?.badgeClass ?? 'bg-gray-50 text-gray-700 border-gray-200')
    : 'bg-gray-50 text-gray-700 border-gray-200';
  const actionLabel = getRecommendationLabel(row);
  const queue = getQueue(row);
  const queueMeta = QUEUE_META[queue];
  const tierLabel = row.risk_tier ? TIER_LABEL[row.risk_tier] : 'Unknown';
  const confidenceText =
    row.confidence == null ? '--' : `${Math.round(row.confidence * 100)}%`;

  return (
    <div
      className="w-full text-left rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all p-4"
    >
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`/matters/${row.matter_id}/risk`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <MatterLink
                entityId={row.matter_id}
                disableNavigation
                className="text-sm sm:text-base font-semibold text-gray-900 truncate max-w-[40rem] border-none cursor-pointer"
              >
                {row.matter_name || 'Unnamed Matter'}
              </MatterLink>
            </a>
            <Badge variant="outline" className={cn('text-xs', actionClass)}>
              {actionLabel}
            </Badge>
            <Badge
              variant="outline"
              className={cn('text-xs', queueMeta.badgeClass)}
            >
              {queueMeta.label}
            </Badge>
            {row.low_confidence_urgent && (
              <Badge
                variant="outline"
                className="text-xs bg-red-50 text-red-700 border-red-200"
              >
                Low Confidence Urgent
              </Badge>
            )}
          </div>

          <div className="text-xs text-gray-600 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" />
              {row.firm_name || 'Unknown Firm'}
            </span>
            <span className="inline-flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {row.representing_attorney_name || 'Unassigned Attorney'}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {row.managing_paralegal_name || 'Unassigned Paralegal'}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              DOA: {formatShortDate(row.date_of_loss)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              Created: {formatShortDate(row.matter_created_at)}
            </span>
            <span>Stage: {row.status || 'Unknown'}</span>
            <span>Tier: {tierLabel}</span>
            {row.coverage_tier && <span>Coverage Tier: {row.coverage_tier}</span>}
            {formatLimit(row.limit_for_scoring, row.limit_source) && (
              <span>Limit: {formatLimit(row.limit_for_scoring, row.limit_source)}</span>
            )}
          </div>

          {row.summary_short && (
            <p className="text-sm text-gray-700 leading-relaxed line-clamp-2">
              {getSummaryOverview(row.summary_short)}
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap pt-1">
            {row.policy_search_recommended && row.policy_search_urgency && (
              <Badge
                variant="outline"
                className={cn(
                  'text-xs',
                  POLICY_URGENCY_CLASS[row.policy_search_urgency],
                )}
              >
                Policy Search: {row.policy_search_urgency}
              </Badge>
            )}
            {row.stale_or_incomplete && (
              <Badge
                variant="outline"
                className="text-xs bg-amber-50 text-amber-700 border-amber-200"
              >
                Snapshot Incomplete
              </Badge>
            )}
            {row.net_projection_below_25 && (
              <Badge
                variant="outline"
                className="text-xs bg-red-50 text-red-700 border-red-200"
              >
                Client Net &lt; 25%
              </Badge>
            )}
            {row.client_sentiment && (() => {
              const TIcon = row.client_sentiment_trend
                ? TREND_ICON[row.client_sentiment_trend].icon
                : null;
              const trendClass = row.client_sentiment_trend
                ? TREND_ICON[row.client_sentiment_trend].className
                : '';
              const sentimentBadge = (
                <Badge
                  variant="outline"
                  className={cn(
                    'text-xs gap-1',
                    SENTIMENT_CLASS[row.client_sentiment],
                    row.client_sentiment_reasoning && 'cursor-help',
                  )}
                >
                  Sentiment: {row.client_sentiment === 'POSITIVE' ? 'Positive' : row.client_sentiment === 'NEGATIVE' ? 'Negative' : 'Neutral'}
                  {TIcon && <TIcon className={cn('h-3 w-3', trendClass)} />}
                </Badge>
              );
              if (!row.client_sentiment_reasoning) return sentimentBadge;
              return (
                <Tooltip>
                  <TooltipTrigger asChild>{sentimentBadge}</TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs font-normal">
                    <p className="text-xs leading-relaxed">
                      &ldquo;{row.client_sentiment_reasoning}&rdquo;
                    </p>
                  </TooltipContent>
                </Tooltip>
              );
            })()}
            {/* v2: Care path signal badges */}
            {row.milestone_status && row.milestone_status !== 'ON_TRACK' && row.milestone_status !== 'NOT_APPLICABLE' && (
              <Badge
                variant="outline"
                className={cn(
                  'text-xs',
                  row.milestone_status === 'OVERDUE' && 'bg-orange-50 text-orange-700 border-orange-200',
                  row.milestone_status === 'BLOCKED' && 'bg-red-50 text-red-700 border-red-200',
                  row.milestone_status === 'AT_RISK' && 'bg-yellow-50 text-yellow-700 border-yellow-200',
                )}
              >
                Milestone: {row.milestone_status.replace('_', ' ')}
              </Badge>
            )}
            {row.pain_progression_state === 'WORSENING' && (
              <Badge
                variant="outline"
                className="text-xs bg-red-50 text-red-700 border-red-200"
              >
                Pain Worsening
              </Badge>
            )}
            {row.action_urgency === 'IMMEDIATE' && (
              <Badge
                variant="outline"
                className="text-xs bg-red-50 text-red-700 border-red-200"
              >
                Immediate Action
              </Badge>
            )}
          </div>
        </div>

        <div className="text-right flex-shrink-0 flex flex-col items-end gap-1 min-w-36">
          <div className="text-xs text-gray-500">
            {formatUpdated(row.snapshot_created_at)}
          </div>
          <div className="text-xs text-gray-500">
            Risk {row.risk_score ?? '--'} • {tierLabel}
          </div>
          <div className="text-xs text-gray-500">Confidence {confidenceText}</div>
          <ArrowRight className="h-4 w-4 text-gray-400 mt-1" />
        </div>
      </div>
    </div>
  );
};

export const RiskOverviewPage: React.FC = () => {
  const [selectedFirmIds, setSelectedFirmIds] = useState<string[]>([]);
  const [selectedAttorneyIds, setSelectedAttorneyIds] = useState<string[]>([]);
  const [selectedParalegalIds, setSelectedParalegalIds] = useState<string[]>(
    [],
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTiers, setSelectedTiers] = useState<RiskTier[]>([]);
  const [queueFilter, setQueueFilter] = useState<QueueFilter>('all');
  const [groupBy, setGroupBy] = useState<GroupBy>('recommendation');
  const [searchQuery, setSearchQuery] = useState('');

  const queryParams: RiskOverviewParams = useMemo(() => {
    const params: RiskOverviewParams = {};
    if (selectedFirmIds.length > 0) params.firm = selectedFirmIds;
    if (selectedAttorneyIds.length > 0) {
      params.representing_attorney = selectedAttorneyIds;
    }
    if (selectedParalegalIds.length > 0) {
      params.managing_paralegal = selectedParalegalIds;
    }
    if (selectedStatuses.length > 0) params.status = selectedStatuses;
    if (selectedTiers.length > 0) params.risk_tier = selectedTiers;
    return params;
  }, [
    selectedFirmIds,
    selectedAttorneyIds,
    selectedParalegalIds,
    selectedStatuses,
    selectedTiers,
  ]);

  const { data, isLoading, isFetching } = useGetRiskOverviewQuery(queryParams);
  const { data: metadata } = useGetMatterListMetadataQuery();

  const allRows = useMemo(
    () => ((data?.matters ?? []) as RiskOverviewRow[]),
    [data?.matters],
  );

  const searchedRows = useMemo(() => {
    const query = normalizeText(searchQuery);
    if (!query) return allRows;
    return allRows.filter((row) => {
      const haystack = [
        row.matter_name,
        row.summary_short,
        row.firm_name,
        row.representing_attorney_name,
        row.managing_paralegal_name,
        row.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [allRows, searchQuery]);

  const queueCounts = useMemo(() => {
    let urgent = 0;
    let demand = 0;
    let escalate = 0;
    let gather = 0;
    let policy = 0;
    let stale = 0;
    let monitor = 0;

    searchedRows.forEach((row) => {
      const queue = getQueue(row);
      if (queue === 'urgent') urgent += 1;
      else if (queue === 'demand') demand += 1;
      else if (queue === 'escalate') escalate += 1;
      else if (queue === 'gather') gather += 1;
      else if (queue === 'policy') policy += 1;
      else if (queue === 'stale') stale += 1;
      else monitor += 1;
    });

    return { urgent, demand, escalate, gather, policy, stale, monitor };
  }, [searchedRows]);

  const queueFiltered = useMemo(() => {
    if (queueFilter === 'all') return searchedRows;
    return searchedRows.filter((row) => getQueue(row) === queueFilter);
  }, [searchedRows, queueFilter]);

  const sortedRows = useMemo(() => {
    return [...queueFiltered].sort((a, b) => {
      const rankDiff = getSortRank(b) - getSortRank(a);
      if (rankDiff !== 0) return rankDiff;
      const aTs = a.snapshot_created_at
        ? new Date(a.snapshot_created_at).getTime()
        : 0;
      const bTs = b.snapshot_created_at
        ? new Date(b.snapshot_created_at).getTime()
        : 0;
      return bTs - aTs;
    });
  }, [queueFiltered]);

  const groupedRows = useMemo(
    () => groupRows(sortedRows, groupBy),
    [sortedRows, groupBy],
  );

  const firmOptions = useMemo(
    () => (metadata?.firms ?? []).map((f) => ({ value: f.id, label: f.name })),
    [metadata],
  );
  const attorneyOptions = useMemo(
    () =>
      (metadata?.attorneys ?? []).map((a) => ({ value: a.id, label: a.name })),
    [metadata],
  );
  const paralegalOptions = useMemo(
    () =>
      (metadata?.managing_paralegals ?? []).map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [metadata],
  );
  const statusOptions = useMemo(
    () =>
      (metadata?.status_options ?? []).map((s) => ({
        value: s.value,
        label: s.label,
      })),
    [metadata],
  );

  const tierOptions = [
    { value: 'CRITICAL', label: 'Critical' },
    { value: 'HIGH', label: 'High' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LOW', label: 'Low' },
    { value: 'UNKNOWN', label: 'Unknown' },
  ];

  const resetFilters = () => {
    setSelectedFirmIds([]);
    setSelectedAttorneyIds([]);
    setSelectedParalegalIds([]);
    setSelectedStatuses([]);
    setSelectedTiers([]);
    setQueueFilter('all');
    setGroupBy('recommendation');
    setSearchQuery('');
  };

  return (
    <div className="max-w-[1440px] mx-auto px-4 py-6 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <MoBrainIcon className="h-6 w-6" />
            <h1 className="text-2xl font-semibold text-gray-900">
              Risk Overview
            </h1>
            {isFetching && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
            )}
          </div>
          <p className="text-sm text-gray-600 max-w-4xl">
            Latest MoBrain snapshot per matter. This assessment combines
            treatment progress, billing, coverage, and evidence signals to
            recommend the next operational action for legal ops teams.
          </p>
        </div>
        <button
          type="button"
          onClick={resetFilters}
          className="text-xs font-medium rounded-lg border border-gray-200 px-3 py-1.5 text-gray-600 hover:bg-gray-50"
        >
          Reset Filters
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
        <QueueMetricCard
          label="Urgent Now"
          value={queueCounts.urgent}
          sub="Immediate attorney escalation"
          tone="critical"
          active={queueFilter === 'urgent'}
          onClick={() => setQueueFilter(queueFilter === 'urgent' ? 'all' : 'urgent')}
        />
        <QueueMetricCard
          label="Demand Review"
          value={queueCounts.demand}
          sub="Approaching treatment/limit cutoff"
          tone="warn"
          active={queueFilter === 'demand'}
          onClick={() => setQueueFilter(queueFilter === 'demand' ? 'all' : 'demand')}
        />
        <QueueMetricCard
          label="Escalate Care"
          value={queueCounts.escalate}
          sub="Conservative care needs specialist"
          tone="warn"
          active={queueFilter === 'escalate'}
          onClick={() => setQueueFilter(queueFilter === 'escalate' ? 'all' : 'escalate')}
        />
        <QueueMetricCard
          label="Needs Outreach"
          value={queueCounts.gather}
          sub="Contact client or confirm coverage"
          tone="warn"
          active={queueFilter === 'gather'}
          onClick={() => setQueueFilter(queueFilter === 'gather' ? 'all' : 'gather')}
        />
        <QueueMetricCard
          label="Policy Search"
          value={queueCounts.policy}
          sub="High/critical policy search urgency"
          tone="warn"
          active={queueFilter === 'policy'}
          onClick={() => setQueueFilter(queueFilter === 'policy' ? 'all' : 'policy')}
        />
        <QueueMetricCard
          label="Stale or Incomplete"
          value={queueCounts.stale}
          sub="Snapshot quality needs refresh"
          tone="warn"
          active={queueFilter === 'stale'}
          onClick={() => setQueueFilter(queueFilter === 'stale' ? 'all' : 'stale')}
        />
        <QueueMetricCard
          label="Total In View"
          value={searchedRows.length}
          sub="After filters/search"
          tone="neutral"
          active={queueFilter === 'all'}
          onClick={() => setQueueFilter('all')}
        />
      </div>

      <Card>
        <CardContent className="p-3 md:p-4 space-y-3">
          <div className="grid gap-2 lg:grid-cols-[minmax(260px,1fr)_repeat(5,minmax(170px,1fr))]">
            <div className="relative min-w-[240px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search matter, summary, firm, attorney..."
                className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <MultiListbox
              options={firmOptions}
              value={selectedFirmIds}
              onChange={setSelectedFirmIds}
              placeholder="All Firms"
            />
            <MultiListbox
              options={attorneyOptions}
              value={selectedAttorneyIds}
              onChange={setSelectedAttorneyIds}
              placeholder="All Attorneys"
            />
            <MultiListbox
              options={paralegalOptions}
              value={selectedParalegalIds}
              onChange={setSelectedParalegalIds}
              placeholder="All Paralegals"
            />
            <MultiListbox
              options={statusOptions}
              value={selectedStatuses}
              onChange={setSelectedStatuses}
              placeholder="All Stages"
            />
            <MultiListbox
              options={tierOptions}
              value={selectedTiers}
              onChange={(values) => setSelectedTiers(values as RiskTier[])}
              placeholder="All Tiers"
            />
          </div>

          <div className="flex items-center justify-end">
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-0.5">
              {([
                { value: 'recommendation', label: 'By Recommendation' },
                { value: 'firm', label: 'By Firm' },
                { value: 'attorney', label: 'By Attorney' },
              ] as { value: GroupBy; label: string }[]).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setGroupBy(option.value)}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                    groupBy === option.value
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {!isLoading && sortedRows.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <BellRing className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-700">
              No matters match this risk view
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Adjust filters or search to expand the queue.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        sortedRows.length > 0 &&
        [...groupedRows.entries()].map(([groupName, rows]) => {
          const urgentInGroup = rows.filter((r) => getQueue(r) === 'urgent').length;
          return (
            <Card key={groupName}>
              <CardHeader className="pt-3 pb-1 px-4">
                <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  {groupBy === 'firm' && (
                    <Building2 className="h-4 w-4 text-gray-500" />
                  )}
                  {groupBy === 'attorney' && (
                    <User className="h-4 w-4 text-gray-500" />
                  )}
                  {groupBy === 'recommendation' && (
                    <Filter className="h-4 w-4 text-gray-500" />
                  )}
                  {groupName}
                  <Badge variant="secondary" className="text-xs ml-1">
                    {rows.length}
                  </Badge>
                  {urgentInGroup > 0 && (
                    <Badge
                      variant="outline"
                      className="text-xs bg-red-50 text-red-700 border-red-200"
                    >
                      {urgentInGroup} urgent
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {rows.map((row) => (
                  <MatterTriageRow key={row.matter_id} row={row} />
                ))}
              </CardContent>
            </Card>
          );
        })}

      {!isLoading && (
        <div className="text-[11px] text-gray-500 flex items-center gap-1 px-1">
          <HelpCircle className="h-3.5 w-3.5" />
          Grouping defaults to recommendation so managers can scan action queues
          first. Risk score remains context, not the primary signal.
        </div>
      )}
    </div>
  );
};
